import React, { useEffect } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { LEGAL_DOCUMENTS, type LegalDocId } from '../data/legalDocuments';
export function LegalDocumentPage({
  docId
}: {
  docId: LegalDocId;
}) {
  const {
    lang
  } = useLanguage();
  const doc = LEGAL_DOCUMENTS[docId];
  // Most legal docs are authored in RU + KK only; fall back to RU when a locale is missing.
  const content = doc[lang as 'ru' | 'kk' | 'en'] || doc.ru;
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [docId]);
  return <div className="min-h-screen bg-[#FAF7F2]" data-id="element-392" data-mp="9a0qaiwuvoqr">
      <Navbar data-id="element-393" data-mp="99r12xjmxlm4" />

      {/* Decorative top gradient */}
      <div className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(124,77,255,0.10) 0%, transparent 70%)'
    }} data-id="element-394" data-mp="2632lcx8j3m4l" />

      <main className="relative pt-28 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-395" data-mp="1iuhr2idb6sj">
        <div className="max-w-3xl mx-auto" data-id="element-396" data-mp="3sv5ykn5m5qhg">
          {/* Back link */}
          <a href="#" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C4DFF] hover:text-[#651FFF] transition-colors mb-8" data-id="element-397" data-mp="3nwun67zo77x4">
            <ArrowLeftIcon size={15} strokeWidth={2.2} data-id="element-398" data-mp="kgf86e0s6df9" />
            {content.backToHome.replace('← ', '')}
          </a>

          {/* Header */}
          <header className="mb-12" data-id="element-399" data-mp="fidmrmfvu20m">
            <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] bg-[#EDE7F6] px-3 py-1.5 rounded-full mb-5" data-id="element-400" data-mp="ff7y28b6125f">
              {content.badge}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-gray-900 leading-[1.15] tracking-tight mb-4" data-id="element-401" data-mp="6iqqff20fmef">
              {content.title}
            </h1>
            {content.updated && <p className="text-sm text-gray-500 font-medium" data-id="element-402" data-mp="2r97w6pld8hr2">
                {content.updated}
              </p>}
          </header>

          {/* Document card */}
          <article className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 sm:p-10 lg:p-14" data-id="element-403" data-mp="2xmc2bzxu2adu">
            {/* Intro */}
            {content.intro.length > 0 && <div className="space-y-4 mb-10" data-id="element-404" data-mp="2nnmjo4y44lwj">
                {content.intro.map((p, i) => <p key={i} className="text-[15px] sm:text-base text-gray-700 leading-relaxed" data-id="element-405" data-mp="29wrb6trj06th" data-mpi={i}>
                    {p}
                  </p>)}
                {content.agreement && <div className="mt-6 p-4 rounded-2xl border-l-4 border-[#7C4DFF] bg-[#F5F3FF]" data-id="element-406" data-mp="24y6h7c2vajdh">
                    <p className="text-[15px] text-[#5B21B6] font-semibold leading-relaxed" data-id="element-407" data-mp="1qyx08a6u7l8g">
                      {content.agreement}
                    </p>
                  </div>}
              </div>}

            {/* Sections */}
            <div className="space-y-12" data-id="element-408" data-mp="1ce2ft616e5y8">
              {content.sections.map((section, si) => <section key={si} className="scroll-mt-28" data-id="element-409" data-mp="17ezmgiuk5o63" data-mpi={si}>
                  <div className="flex items-baseline gap-3 mb-4" data-id="element-410" data-mp="1ldphzu1qbfsc">
                    {section.number && <span className="text-xs font-bold text-[#7C4DFF] tracking-widest" data-id="element-411" data-mp="1ha9pn9wp01lg">
                        {section.number}
                      </span>}
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" data-id="element-412" data-mp="1qcix1qwnzeum">
                      {section.title}
                    </h2>
                  </div>

                  <div className="h-px bg-gray-100 mb-5" data-id="element-413" data-mp="2010txx1tp1sz" />

                  {section.intro && <p className="text-[15px] text-gray-700 leading-relaxed mb-4" data-id="element-414" data-mp="2e0se6fixneff">
                      {section.intro}
                    </p>}

                  {section.body && section.body.map((p, i) => <p key={i} className="text-[15px] text-gray-700 leading-relaxed mb-3" data-id="element-415" data-mp="7ct6nzwygrgl" data-mpi={i}>
                        {p}
                      </p>)}

                  {section.groups && section.groups.map((group, gi) => <div key={gi} className={gi > 0 ? 'mt-5' : 'mt-2'} data-id="element-416" data-mp="3kjutv318fa74" data-mpi={gi}>
                        {group.heading && <p className="text-sm font-semibold text-gray-900 mb-2.5" data-id="element-417" data-mp="3phwf9um4rllr">
                            {group.heading}
                          </p>}
                        <ul className="space-y-2" data-id="element-418" data-mp="2ehuhij6u4v7">
                          {group.items.map((item, ii) => <li key={ii} className="flex items-start gap-3 text-[15px] text-gray-700 leading-relaxed" data-id="element-419" data-mp="18utulp5klwtf" data-mpi={ii}>
                              <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-[#7C4DFF]" aria-hidden="true" data-id="element-420" data-mp="1nw0rb82oidjp" />
                              <span data-id="element-421" data-mp="1e5pmzkf322ca">{item}</span>
                            </li>)}
                        </ul>
                      </div>)}

                  {section.outro && <p className="text-[15px] text-gray-700 leading-relaxed mt-4" data-id="element-422" data-mp="1547wr97yok9v">
                      {section.outro}
                    </p>}
                </section>)}
            </div>
          </article>
        </div>
      </main>

      <Footer data-id="element-423" data-mp="r84f0fe9y4ea" />
    </div>;
}